import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  Clock,
  MoreHorizontal,
  Plus,
} from "lucide-react";

import { formatRecipientName, stripHtml } from "../../utils/letterBoard";
import {
  getSendableRecipients,
  recipientReceivedRingClass,
} from "../../utils/letterSend";

/** Ignore card open when the gesture was a column/list swipe. */
const TAP_MOVE_THRESHOLD_PX = 12;

function LetterCardMenu({ onEdit, onDelete, onSendNow, showSendNow }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className="rounded-md p-1 text-textMuted transition hover:bg-dashboardColumn hover:text-secondaryText"
        aria-label="Options"
      >
        <MoreHorizontal size={18} />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          />
          <div className="absolute right-0 top-full z-20 mt-1 min-w-[140px] rounded-xl border border-greyLight/60 bg-white py-1 shadow-lg">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onEdit();
              }}
              className="block w-full px-4 py-2 text-left font-josefin text-sm text-secondaryText hover:bg-dashboardColumn"
            >
              Modifier
            </button>
            {showSendNow && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onSendNow();
                }}
                className="block w-full px-4 py-2 text-left font-josefin text-sm text-secondaryText hover:bg-dashboardColumn"
              >
                Envoyer maintenant
              </button>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onDelete();
              }}
              className="block w-full px-4 py-2 text-left font-josefin text-sm text-red hover:bg-dashboardColumn"
            >
              Supprimer
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function DeliveryStatus({ recipients }) {
  if (!recipients?.length) return null;

  const allReceived = recipients.every((r) => r.deliveryStatus === "received");
  const anyInProgress = recipients.some(
    (r) => r.deliveryStatus === "in_progress" || r.deliveryStatus === "pending"
  );

  if (allReceived) {
    return (
      <div className="inline-flex items-center gap-1.5 rounded-full bg-sage/15 px-2.5 py-1 font-josefin text-xs font-medium text-sageDark">
        <CheckCircle2 size={13} />
        Reçue
      </div>
    );
  }

  if (anyInProgress || !recipients[0]?.deliveryStatus) {
    return (
      <div className="inline-flex items-center gap-1.5 rounded-full bg-brandPurple/10 px-2.5 py-1 font-josefin text-xs font-medium text-brandPurple">
        <Clock size={13} />
        En cours…
      </div>
    );
  }

  return null;
}

function RecipientChips({ recipients }) {
  if (!recipients?.length) return null;

  const visible = recipients.slice(0, 3);
  const overflow = recipients.length - visible.length;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((r, i) => (
        <span
          key={r._id || i}
          className={`inline-flex items-center rounded-full bg-dashboardColumn px-2.5 py-0.5 font-josefin text-xs text-textMuted ${recipientReceivedRingClass(r)}`}
        >
          {formatRecipientName(r)}
        </span>
      ))}
      {overflow > 0 && (
        <span className="inline-flex items-center rounded-full bg-dashboardColumn px-2.5 py-0.5 font-josefin text-xs text-textMuted">
          +{overflow}
        </span>
      )}
    </div>
  );
}

function LetterCard({
  letter,
  column,
  onDelete,
  onSendNow,
}) {
  const navigate = useNavigate();
  const pointerRef = useRef({ x: 0, y: 0, dragged: false });
  const { _id, title, content, recipients = [] } = letter;
  const preview = stripHtml(content) || "Écris ta lettre ici.";
  const sendableCount = getSendableRecipients(recipients).length;
  const showSendNow = column === "scheduled" && sendableCount > 0;

  // Sent column opens a read-only view of the recipients that were actually sent.
  // Scheduled/draft keep the editable letter (remaining recipients only).
  const openLetter = () =>
    navigate(column === "sent" ? `/letter/${_id}?view=sent` : `/letter/${_id}`);
  const openAddRecipient = () => navigate(`/letter/${_id}?addRecipient=1`);

  const onPointerDown = (event) => {
    if (event.button != null && event.button !== 0) return;
    pointerRef.current = {
      x: event.clientX,
      y: event.clientY,
      dragged: false,
    };
  };

  const onPointerMove = (event) => {
    const state = pointerRef.current;
    if (state.dragged) return;
    const dx = Math.abs(event.clientX - state.x);
    const dy = Math.abs(event.clientY - state.y);
    if (dx > TAP_MOVE_THRESHOLD_PX || dy > TAP_MOVE_THRESHOLD_PX) {
      state.dragged = true;
    }
  };

  const onCardClick = () => {
    // Swipe started on this card — let the board/list own the gesture.
    if (pointerRef.current.dragged) return;
    openLetter();
  };

  return (
    <article
      className="group cursor-pointer rounded-xl border border-greyLight/30 bg-white p-4 shadow-sm transition hover:shadow-md"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onClick={onCardClick}
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <h3 className="font-josefin text-base font-semibold text-black line-clamp-1">
          {title || "Sans titre"}
        </h3>
        {column !== "sent" && (
          <LetterCardMenu
            onEdit={openLetter}
            onDelete={() => onDelete(letter)}
            onSendNow={() => onSendNow(letter)}
            showSendNow={showSendNow}
          />
        )}
      </div>

      <p className="mb-4 line-clamp-3 font-josefin text-sm leading-relaxed text-textMuted">
        {preview}
      </p>

      {column === "draft" && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openAddRecipient();
          }}
          className="flex items-center gap-1.5 rounded-lg border border-brandPurple bg-white px-3 py-1.5 font-josefin text-xs font-medium text-brandPurple transition hover:bg-brandPurple/5"
        >
          <Plus size={13} />
          Destinataire
        </button>
      )}

      {showSendNow && (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSendNow(letter);
            }}
            className="w-full rounded-lg bg-sageDark py-2 font-josefin text-sm font-medium text-white transition hover:bg-sage"
          >
            Envoyer maintenant
          </button>
          <RecipientChips recipients={recipients} />
        </div>
      )}

      {column === "scheduled" && !showSendNow && (
        <RecipientChips recipients={recipients} />
      )}

      {column === "sent" && (
        <div className="flex flex-col gap-2">
          <DeliveryStatus recipients={recipients} />
          <RecipientChips recipients={recipients} />
        </div>
      )}
    </article>
  );
}

export default LetterCard;
